import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import aeLogo from '@/assets/AE_Logo_White.webp';

const BOOKING_URL = 'https://form.fillout.com/t/6Qfrr3aggsus';

const brands = [
  { key: 'laContessina', id: 'la-contessina' },
  { key: 'barjBuzzoni', id: 'barj-buzzoni' },
  { key: 'pataviumart', id: 'pataviumart' },
  { key: 'italianStoneSource', id: 'italian-stone-source' },
];

const navSections = [
  { key: 'showroom', id: 'showroom' },
  { key: 'leMaisons', id: 'la-contessina' },
  { key: 'servizi', id: 'servizi' },
  { key: 'contatti', id: 'contatti' },
];

const brandSectionIds = ['la-contessina', 'barj-buzzoni', 'pataviumart', 'italian-stone-source'];

const NavButton = memo(({ navKey, active, onClick, children, className = '' }: { navKey: string; active: boolean; onClick: () => void; children: React.ReactNode; className?: string }) => (
  <button onClick={onClick} className={`relative px-5 py-2 text-sm tracking-wide transition-colors ${active ? 'text-background font-medium' : 'text-foreground/80 hover:text-foreground'} ${className}`}>
    {active && (
      <motion.div
        layoutId="activeNavPill"
        className="absolute inset-0 bg-foreground rounded-full"
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      />
    )}
    <span className="relative z-10 flex items-center gap-1">{children}</span>
  </button>
));
NavButton.displayName = 'NavButton';

const getActiveNavKey = (activeSection: string): string => {
  if (activeSection === 'showroom') return 'showroom';
  if (brandSectionIds.includes(activeSection)) return 'leMaisons';
  if (activeSection === 'servizi') return 'servizi';
  if (activeSection === 'contatti') return 'contatti';
  return '';
};

const Header = ({ onBooking }: { onBooking?: () => void }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [maisonOpen, setMaisonOpen] = useState(false);
  const [activeNavKey, setActiveNavKey] = useState('');
  const [activeBrandId, setActiveBrandId] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const rafRef = useRef<number>(0);
  const headerRef = useRef<HTMLElement>(null);

  // Close mobile menu on click outside
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    // Use setTimeout to avoid capturing the opening click
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);

        const sections = ['showroom', ...brandSectionIds, 'servizi', 'contatti'];
        let current = '';
        for (const id of sections) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 120) current = id;
          }
        }
        setActiveNavKey(getActiveNavKey(current));
        setActiveBrandId(brandSectionIds.includes(current) ? current : '');

        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(docHeight > 0 ? Math.min(window.scrollY / docHeight, 1) : 0);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
    setMaisonOpen(false);
  };

  return (
    <motion.header
      ref={headerRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        mobileOpen
          ? 'bg-background/95 border-b border-border shadow-lg'
          : scrolled
            ? 'bg-background/95 border-b border-border shadow-lg'
            : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={() => scrollToSection('hero')} className="hover:opacity-80 transition-opacity">
            <img src={aeLogo} alt="Atelier Elements" className="h-8" />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavButton navKey="showroom" active={activeNavKey === 'showroom'} onClick={() => scrollToSection('showroom')}>
              {t('nav.showroom')}
            </NavButton>

            {/* Le Maisons Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMaisonOpen(true)}
              onMouseLeave={() => setMaisonOpen(false)}
            >
              <NavButton navKey="leMaisons" active={activeNavKey === 'leMaisons'} onClick={() => {}} className="flex items-center gap-1">
                {t('nav.leMaisons')}
                <ChevronDown className="w-3.5 h-3.5 relative z-10" />
              </NavButton>
              <AnimatePresence>
                {maisonOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-card border border-border shadow-xl rounded-lg overflow-hidden z-50"
                  >
                    {brands.map((brand) => (
                      <button
                        key={brand.key}
                        onClick={() => scrollToSection(brand.id)}
                        className={`block w-full text-left px-5 py-3 text-sm transition-colors ${
                          activeBrandId === brand.id
                            ? 'text-foreground bg-secondary/50 font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                        }`}
                      >
                        {t(`brands.${brand.key}.name`)}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavButton navKey="servizi" active={activeNavKey === 'servizi'} onClick={() => scrollToSection('servizi')}>
              {t('nav.servizi')}
            </NavButton>

            <NavButton navKey="contatti" active={activeNavKey === 'contatti'} onClick={() => scrollToSection('contatti')}>
              {t('nav.contatti')}
            </NavButton>
          </nav>

          {/* Right side: Lang + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <button
              onClick={onBooking}
              className="px-6 py-2.5 text-sm tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-full font-medium"
            >
              {t('nav.bookVisit')}
            </button>
          </div>

          {/* Mobile burger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-foreground"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background/70 backdrop-blur-xl border-b border-border overflow-hidden"
          >
            <div className="px-6 py-6 space-y-4 text-right">
              <button onClick={() => scrollToSection('showroom')} className="block w-full text-right text-sm tracking-wide text-foreground/80 hover:text-foreground">
                {t('nav.showroom')}
              </button>
              <div className="space-y-2">
                <span className="block text-right text-sm tracking-wide text-primary font-medium">
                  {t('nav.leMaisons')}
                </span>
                {brands.map((brand) => (
                  <button
                    key={brand.key}
                    onClick={() => scrollToSection(brand.id)}
                    className="block w-full text-right text-sm text-foreground/70 hover:text-foreground"
                  >
                    {t(`brands.${brand.key}.name`)}
                  </button>
                ))}
              </div>
              <button onClick={() => scrollToSection('servizi')} className="block w-full text-right text-sm tracking-wide text-foreground/80 hover:text-foreground">
                {t('nav.servizi')}
              </button>
              <button onClick={() => scrollToSection('contatti')} className="block w-full text-right text-sm tracking-wide text-foreground/80 hover:text-foreground">
                {t('nav.contatti')}
              </button>
              <div className="pt-4 border-t border-border flex items-center justify-between gap-4">
                <LanguageSwitcher dropUp />
                <button
                  onClick={() => { setMobileOpen(false); onBooking?.(); }}
                  className="px-6 py-2.5 text-sm tracking-wide bg-primary text-primary-foreground rounded-full font-medium"
                >
                  {t('nav.bookVisit')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Scroll progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-transparent">
        <div
          className="h-full bg-primary transition-none"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>
    </motion.header>
  );
};

export default Header;
